import { KeyboardEvent, useEffect, useRef, useState } from 'react';

import { MultiTextAreaProps, ValidationErrorType } from './multi-text-area.component';

interface ViewProps {
    inputValue: string;
    setInputValue: React.Dispatch<React.SetStateAction<string>>;
    touched: boolean;
    validationErrorType: ValidationErrorType | null;
    inputRef: React.RefObject<HTMLInputElement | null>;
    containerRef: React.RefObject<HTMLDivElement | null>;
    values: string[];
    isValid: boolean;
    addValue: (value: string) => void;
    removeValue: (index: number) => void;
    handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
    handleBlur: () => void;
    handleContainerClick: () => void;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleFocus: () => void;
}

const useMultiTextArea = (props: MultiTextAreaProps): ViewProps => {
    const [inputValue, setInputValue] = useState<string>('');
    const [touched, setTouched] = useState<boolean>(false);
    const [validationErrorType, setValidationErrorType] = useState<ValidationErrorType | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const values = props.values ?? [];
    const isValid = props.required ? values.length > 0 : true;

    const addValue = (value: string) => {
        const trimmedValue = value.trim();
        if (!trimmedValue) return;

        // Check for duplicates
        if (values.includes(trimmedValue)) {
            setValidationErrorType('duplicate');
            return;
        }

        // Run custom validation if provided
        if (props.validateValue && !props.validateValue(trimmedValue)) {
            setValidationErrorType('invalid');
            return;
        }

        setValidationErrorType(null);
        const newValues = [...values, trimmedValue];
        props.onChange?.(newValues);
        setInputValue('');
    };

    const removeValue = (index: number) => {
        const newValues = values.filter((_, i) => i !== index);
        props.onChange?.(newValues);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            if (inputValue.trim()) {
                e.preventDefault();
                addValue(inputValue);
            }
        } else if (e.key === 'Backspace' && !inputValue && values.length > 0) {
            // Remove last tag when backspace is pressed on empty input
            removeValue(values.length - 1);
        }
    };

    const handleBlur = () => {
        setTouched(true);
        if (inputValue.trim()) {
            addValue(inputValue);
        }
    };

    const handleContainerClick = () => {
        inputRef.current?.focus();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        setValidationErrorType(null);
    };

    const handleFocus = () => {
        setTouched(true);
    };

    useEffect(() => {
        if (props.submitted) {
            !touched && setTouched(true);
        }
    }, [props.submitted]);

    useEffect(() => {
        if (props.isValidCallback) {
            props.isValidCallback(isValid && !validationErrorType);
        }
    }, [values, validationErrorType, isValid]);

    return {
        inputValue,
        setInputValue,
        touched,
        validationErrorType,
        inputRef,
        containerRef,
        values,
        isValid,
        addValue,
        removeValue,
        handleKeyDown,
        handleBlur,
        handleContainerClick,
        handleInputChange,
        handleFocus
    };
};

export default useMultiTextArea;
